import { Menu as BaseMenu } from "@base-ui/react/menu";
import clsx from "clsx";
import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router";
import { Icon, type IconName } from "../Icon/Icon";
import { Kbd } from "../Kbd/Kbd";
import styles from "../Popup/Popup.module.css";

interface MenuRootProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function MenuRoot({ children, open, onOpenChange }: MenuRootProps) {
  return (
    <BaseMenu.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}>
      {children}
    </BaseMenu.Root>
  );
}

/** The element that opens the menu, usually a Button or IconButton. */
function MenuTrigger({ children }: { children: ReactElement }) {
  return <BaseMenu.Trigger render={children} />;
}

interface MenuPopupProps {
  children: ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  className?: string;
}

function MenuPopup({ children, side = "bottom", align = "start", className }: MenuPopupProps) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner className={styles.positioner} side={side} align={align} sideOffset={4} collisionPadding={8}>
        <BaseMenu.Popup className={clsx(styles.popup, className)}>{children}</BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

interface MenuItemProps {
  /** End the label with "…" when it opens a further step ("Rename…"). */
  children: ReactNode;
  onClick?: () => void;
  icon?: IconName;
  /** Shortcut hint, e.g. ["mod", "k"]. */
  shortcut?: string[];
  /** Destructive items sit last, after a separator, and render in --color-danger. */
  danger?: boolean;
  disabled?: boolean;
  closeOnClick?: boolean;
}

function MenuItem({ children, onClick, icon, shortcut, danger, disabled, closeOnClick }: MenuItemProps) {
  return (
    <BaseMenu.Item
      className={styles.item}
      onClick={onClick}
      disabled={disabled}
      closeOnClick={closeOnClick}
      data-danger={danger || undefined}
    >
      <span className={styles.itemIndicator}>{icon ? <Icon name={icon} /> : null}</span>
      <span className={styles.itemText}>{children}</span>
      {shortcut ? <Kbd keys={shortcut} className={styles.itemHint} /> : null}
    </BaseMenu.Item>
  );
}

/** A menu entry that navigates (renders a router <Link>, so Cmd/Ctrl-click opens a new tab). */
function MenuLinkItem({ to, children, icon }: { to: string; children: ReactNode; icon?: IconName }) {
  return (
    <BaseMenu.LinkItem className={styles.item} render={<Link to={to} />}>
      <span className={styles.itemIndicator}>{icon ? <Icon name={icon} /> : null}</span>
      <span className={styles.itemText}>{children}</span>
    </BaseMenu.LinkItem>
  );
}

function MenuSeparator() {
  return <BaseMenu.Separator className={styles.separator} />;
}

function MenuGroup({ label, children }: { label?: ReactNode; children: ReactNode }) {
  return (
    <BaseMenu.Group>
      {label ? <BaseMenu.GroupLabel className={styles.groupLabel}>{label}</BaseMenu.GroupLabel> : null}
      {children}
    </BaseMenu.Group>
  );
}

interface MenuRadioGroupProps<V extends string> {
  label?: ReactNode;
  value: V;
  onValueChange: (value: V) => void;
  children: ReactNode;
}

function MenuRadioGroup<V extends string>({ label, value, onValueChange, children }: MenuRadioGroupProps<V>) {
  return (
    <BaseMenu.Group>
      {label ? <BaseMenu.GroupLabel className={styles.groupLabel}>{label}</BaseMenu.GroupLabel> : null}
      <BaseMenu.RadioGroup value={value} onValueChange={(next: V) => onValueChange(next)}>
        {children}
      </BaseMenu.RadioGroup>
    </BaseMenu.Group>
  );
}

function MenuRadioItem({ value, children, icon }: { value: string; children: ReactNode; icon?: IconName }) {
  return (
    <BaseMenu.RadioItem className={styles.item} value={value} closeOnClick={false}>
      <BaseMenu.RadioItemIndicator className={styles.itemIndicator} keepMounted={false}>
        <Icon name="check" />
      </BaseMenu.RadioItemIndicator>
      <span className={styles.itemText}>{children}</span>
      {icon ? <Icon name={icon} className={styles.itemHint} /> : null}
    </BaseMenu.RadioItem>
  );
}

/**
 * Menus (Base UI Menu):
 * <Menu.Root><Menu.Trigger><IconButton …/></Menu.Trigger><Menu.Popup><Menu.Item onClick>Rename…</Menu.Item></Menu.Popup></Menu.Root>
 */
export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Popup: MenuPopup,
  Item: MenuItem,
  LinkItem: MenuLinkItem,
  Separator: MenuSeparator,
  Group: MenuGroup,
  RadioGroup: MenuRadioGroup,
  RadioItem: MenuRadioItem,
};
